/**
 * OCTOPUS - Página "Ao vivo" do equipamento (item 6.17, rebuild da FASE C2)
 * EquipmentMonitorPage.js
 *
 * Página única /equipments/:id/monitor com DUAS abas:
 * - "Ao vivo" (default): mapa + trilha 24h + seções dinâmicas montadas pelo
 *   CADASTRO do equipamento (GET /live-view — grupos por motor com papel
 *   Propulsão/Draga/Motor; universal p/ draga, pá-carregadeira e estacionário)
 * - "Histórico": gráficos de telemetria (inalterado)
 *
 * A aba inicial respeita ?tab=historico (links/redirects legados).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  RefreshCw,
  Printer,
  MapPin,
  Gauge,
  Fuel,
  Clock,
  Activity,
  History,
  Loader2,
  AlertCircle,
  Check
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import DateTimePicker24h from '@/components/ui/datetime-picker';
import { Skeleton } from '@/components/ui/skeleton';
import ErrorBoundary from '@/components/ErrorBoundary';
import { KPICard } from '@/components/monitor/KPICard';
import PrintExportDialog from '@/components/monitor/PrintExportDialog';
import EquipmentMonitorPrint from '@/pages/EquipmentMonitorPrint';

// Lista canônica de gráficos do relatório PDF (ordem = ordem no PDF).
const PRINT_CHART_OPTIONS = [
  { id: 'speed', label: 'Velocidade', defaultOn: true },
  { id: 'rpm', label: 'RPM dos Motores', defaultOn: true },
  { id: 'fuel', label: 'Consumo de Combustível', defaultOn: true },
  { id: 'temperature', label: 'Temperatura', defaultOn: true },
  { id: 'boost', label: 'Pressão Turbo (Boost)', defaultOn: false },
  { id: 'load', label: 'Carga dos Motores', defaultOn: false },
  { id: 'exhaustTemp', label: 'Temperatura de Escape', defaultOn: false },
];
const PRINT_DEFAULT_SELECTED = PRINT_CHART_OPTIONS.filter(c => c.defaultOn).map(c => c.id);
import { useTimezone } from '@/contexts/TimezoneContext';

// Components
import LiveTab from '@/components/monitor/LiveTab';
import TelemetryCharts from '@/components/monitor/TelemetryCharts';

// v4.0: Agent Update components (FIX 2026-03-04)
import AgentUpdateModal, { AgentUpdateBadge } from '@/components/agent/AgentUpdateModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Períodos disponíveis
const PERIODS = [
  { value: '-1h', label: '1 hora' },
  { value: '-6h', label: '6 horas' },
  { value: '-24h', label: '24 horas' },
  { value: '-7d', label: '7 dias' },
  { value: '-30d', label: '30 dias' },
  { value: 'custom', label: 'Customizado' },
];

// ============================================================================
// HELPERS DE DATA/HORA — TIMEZONE DO EQUIPAMENTO (NÃO DO NAVEGADOR)
// ----------------------------------------------------------------------------
// O filtro de período customizado tem que viver no MESMO timezone que o gráfico
// usa para plotar (TimezoneContext, padrão America/Sao_Paulo). Caso contrário,
// um operador acessando de outro fuso (p.ex. Eastern Time) digita "05:00" no
// filtro pensando em BRT, mas `new Date("2026-05-30T05:00")` interpreta como
// 05:00 ET → o backend devolve dados a partir de 09:00 UTC e o gráfico (que
// formata em BRT) plota a partir de 06:00 BRT — defasagem de 1h percebida.
//
// As funções abaixo formatam um instante UTC como wall-clock no `tz` e parseiam
// um wall-clock interpretando-o no `tz` (com correção para DST). Assim o picker
// e o backend trabalham na mesma régua que o gráfico.
// ============================================================================

const dateTimeLocalPartsCache = new Map();
const getDateTimeLocalParts = (date, tz) => {
  let fmt = dateTimeLocalPartsCache.get(tz);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    dateTimeLocalPartsCache.set(tz, fmt);
  }
  const out = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') out[part.type] = part.value;
  }
  // Intl pode reportar "24" à meia-noite em algumas runtimes — normaliza.
  if (out.hour === '24') out.hour = '00';
  return out;
};

// Formata um Date (instante UTC) como string "YYYY-MM-DDTHH:mm" representando
// o wall-clock daquele instante no timezone `tz`.
const toDateTimeLocalValue = (date, tz) => {
  const p = getDateTimeLocalParts(date, tz);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// Parseia "YYYY-MM-DDTHH:mm" interpretando-o como wall-clock em `tz` e devolve
// o Date UTC correspondente. Faz uma rodada de refinamento para cobrir bordas
// de DST (horários inexistentes/ambíguos), o que basta para tz fixos como BRT.
const parseDateTimeLocalValue = (value, tz) => {
  if (!value || typeof value !== 'string') return null;
  const [datePart, timePart = '00:00'] = value.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (![y, mo, d, hh, mm].every(Number.isFinite)) return null;

  const targetMs = Date.UTC(y, mo - 1, d, hh, mm, 0, 0);
  // Chute inicial: trata o wall-clock como se fosse UTC e corrige pelo offset
  // que o tz reporta naquele instante.
  const p1 = getDateTimeLocalParts(new Date(targetMs), tz);
  const renderedMs1 = Date.UTC(
    +p1.year, +p1.month - 1, +p1.day,
    +p1.hour, +p1.minute, +p1.second,
  );
  let utcMs = targetMs - (renderedMs1 - targetMs);
  // Refina uma vez (cobre transições de DST que mudam o offset entre as duas
  // rodadas; sem efeito em tz sem DST como America/Sao_Paulo desde 2019).
  const p2 = getDateTimeLocalParts(new Date(utcMs), tz);
  const renderedMs2 = Date.UTC(
    +p2.year, +p2.month - 1, +p2.day,
    +p2.hour, +p2.minute, +p2.second,
  );
  if (renderedMs2 !== targetMs) {
    utcMs += targetMs - renderedMs2;
  }
  const result = new Date(utcMs);
  return Number.isNaN(result.getTime()) ? null : result;
};

const HISTORY_MAX_RANGE_MS = 90 * 24 * 60 * 60 * 1000;

const buildTimeRange = (period, customStart, customEnd, tz) => {
  if (period !== 'custom') {
    return { start: period, stop: 'now()', key: period };
  }

  const startDate = parseDateTimeLocalValue(customStart, tz);
  const endDate = parseDateTimeLocalValue(customEnd, tz);
  const hasValidRange = startDate instanceof Date
    && endDate instanceof Date
    && Number.isFinite(startDate.getTime())
    && Number.isFinite(endDate.getTime())
    && endDate.getTime() > startDate.getTime();

  if (!hasValidRange) {
    return { start: '-24h', stop: 'now()', key: 'custom-invalid' };
  }

  const start = startDate.toISOString();
  const stop = endDate.toISOString();
  return { start, stop, key: `custom:${start}:${stop}` };
};

// Abas fixas da página (item 6.17): Ao vivo (default) + Histórico.
// ?tab=historico (e o redirect legado /history) abre direto no Histórico;
// valores legados (posicao/propulsao/combustivel/outros) caem no Ao vivo.
const normalizeTabParam = (tab) => (tab === 'historico' ? 'historico' : 'aovivo');

// Helper para obter headers de autenticação
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// KPICard / KPIValue extraídos para '@/components/monitor/KPICard'.

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const EquipmentMonitorPage = () => {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // `timezone` é o TZ do operacional (default America/Sao_Paulo, configurável em
  // /settings/timezone) e tem que reger o filtro de período customizado, não o
  // TZ do navegador — ver helpers no topo do arquivo para o porquê.
  const { formatTime, timezone } = useTimezone();

  // Estados principais
  const [equipment, setEquipment] = useState(null);
  const [liveView, setLiveView] = useState(null);
  const [statistics, setStatistics] = useState(null);
  
  // Estados de loading/error
  const [loadingEquipment, setLoadingEquipment] = useState(true);
  const [loadingSources, setLoadingSources] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [showStatsLoadingHint, setShowStatsLoadingHint] = useState(false);
  
  // Estados de controle
  const [period, setPeriod] = useState('-24h');
  const [appliedPeriod, setAppliedPeriod] = useState('-24h');
  // Inicializadores rodam uma vez na primeira renderização, quando `timezone`
  // ainda é o default síncrono do TimezoneContext (America/Sao_Paulo). Isso é
  // suficiente para esta instalação; o usuário pode editar à mão se quiser
  // outro fuso.
  const [customStart, setCustomStart] = useState(() => (
    toDateTimeLocalValue(new Date(Date.now() - 24 * 60 * 60 * 1000), timezone)
  ));
  const [customEnd, setCustomEnd] = useState(() => toDateTimeLocalValue(new Date(), timezone));
  const [appliedCustomStart, setAppliedCustomStart] = useState(customStart);
  const [appliedCustomEnd, setAppliedCustomEnd] = useState(customEnd);
  // Aba inicial respeita ?tab= (fix do botão "Histórico" da lista + redirects legados)
  const [activeTab, setActiveTab] = useState(() => normalizeTabParam(searchParams.get('tab')));
  const [lastUpdate, setLastUpdate] = useState(null);
  const [geofences, setGeofences] = useState([]);  // D6 FIX
  const [agentModalOpen, setAgentModalOpen] = useState(false);  // v4.0: Agent update modal
  // Incrementado pelo botão "Atualizar" para forçar re-fetch nos componentes filhos
  // (TelemetryCharts/LiveTab) que gerenciam o próprio fetch e não reagem a um
  // clique sem mudança de período.
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  // Quando preenchido, renderiza o EquipmentMonitorPrint como overlay
  // (invisível na tela, único conteúdo visível em @media print).
  const [printConfig, setPrintConfig] = useState(null);
  const statsRequestRef = useRef(null);
  const timeRange = useMemo(
    () => buildTimeRange(appliedPeriod, appliedCustomStart, appliedCustomEnd, timezone),
    [appliedPeriod, appliedCustomStart, appliedCustomEnd, timezone]
  );
  
  // ========================================================================
  // FETCH FUNCTIONS
  // ========================================================================
  
  const fetchEquipment = useCallback(async () => {
    try {
      setLoadingEquipment(true);
      const res = await axios.get(`${API}/api/equipments/${equipmentId}`, {
        headers: getAuthHeaders()
      });
      setEquipment(res.data);
    } catch (err) {
      console.error('Erro ao buscar equipamento:', err);
      setError('Equipamento não encontrado');
    } finally {
      setLoadingEquipment(false);
    }
  }, [equipmentId]);
  
  const fetchLiveView = useCallback(async () => {
    try {
      // D8 FIX: NÃO setar loading no polling, apenas no fetch inicial
      const res = await axios.get(`${API}/api/equipments/${equipmentId}/live-view`, {
        headers: getAuthHeaders()
      });

      // D8 FIX: Só atualiza state se dados mudaram (evita re-render desnecessário)
      setLiveView(prev => {
        const newData = res.data;
        if (JSON.stringify(prev) === JSON.stringify(newData)) return prev;
        return newData;
      });

      setLastUpdate(new Date());
    } catch (err) {
      console.error('Erro ao buscar live-view:', err);
    }
  }, [equipmentId]);
  
  const fetchStatistics = useCallback(async () => {
    let controller = null;
    try {
      if (statsRequestRef.current) {
        statsRequestRef.current.abort();
      }
      controller = new AbortController();
      statsRequestRef.current = controller;
      setLoadingStats(true);
      setStatsError(null);
      const params = new URLSearchParams({ start: timeRange.start });
      if (timeRange.stop && timeRange.stop !== 'now()') {
        params.set('stop', timeRange.stop);
      }
      const res = await axios.get(
        `${API}/api/equipments/${equipmentId}/signalk/statistics?${params.toString()}`,
        { headers: getAuthHeaders(), timeout: 120000, signal: controller.signal }
      );
      if (statsRequestRef.current !== controller) {
        return;
      }
      setStatistics(res.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Erro ao buscar statistics:', err);
      setStatsError('Não foi possível carregar os indicadores do histórico. Tente atualizar novamente.');
    } finally {
      if (controller && statsRequestRef.current === controller && !controller.signal.aborted) {
        setLoadingStats(false);
      }
    }
  }, [equipmentId, timeRange]);
  
  // D6 FIX: Fetch geofences do equipamento
  const fetchGeofences = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/api/equipments/${equipmentId}/geofences`,
        { headers: getAuthHeaders() }
      );
      setGeofences(res.data || []);
    } catch (err) {
      // Geofences são opcionais - se endpoint não existir, ignora
      console.warn('Geofences não disponíveis:', err.message);
      setGeofences([]);
    }
  }, [equipmentId]);
  
  // D8 FIX: Fetch inicial COM loading state
  const fetchLiveViewInitial = useCallback(async () => {
    try {
      setLoadingSources(true);
      const res = await axios.get(`${API}/api/equipments/${equipmentId}/live-view`, {
        headers: getAuthHeaders()
      });
      setLiveView(res.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Erro ao buscar live-view:', err);
    } finally {
      setLoadingSources(false);
    }
  }, [equipmentId]);
  
  // ========================================================================
  // EFFECTS
  // ========================================================================
  
  // Fetch inicial - chamadas paralelas (D6 FIX: adicionado geofences)
  useEffect(() => {
    Promise.all([
      fetchEquipment(),
      fetchLiveViewInitial(),
      fetchGeofences()
    ]);
  }, [fetchEquipment, fetchLiveViewInitial, fetchGeofences]);

  // D8 FIX: Polling a cada 10s (mais responsivo), só para o live-view (stats é caro).
  // Refresh periódico e limitado (mesmo espírito do INV-UI-POPUP-002: >= 10s).
  useEffect(() => {
    if (activeTab === 'historico') return; // Não faz polling na tab histórico

    const interval = setInterval(() => {
      fetchLiveView();
    }, 10000); // D8 FIX: 10 segundos, não 30

    return () => clearInterval(interval);
  }, [activeTab, fetchLiveView]);

  // Mantém a aba sincronizada com o ?tab= do URL (redirects legados /history)
  useEffect(() => {
    setActiveTab(normalizeTabParam(searchParams.get('tab')));
  }, [searchParams]);
  
  // Recarrega stats somente quando o intervalo aplicado muda.
  useEffect(() => {
    fetchStatistics();
    return () => {
      if (statsRequestRef.current) {
        statsRequestRef.current.abort();
      }
    };
  }, [fetchStatistics]);

  useEffect(() => {
    if (!loadingStats) {
      setShowStatsLoadingHint(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setShowStatsLoadingHint(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [loadingStats, timeRange.key]);
  
  // ========================================================================
  // TABS (item 6.17): fixas — Ao vivo (default) + Histórico
  // ========================================================================

  const TABS = useMemo(() => ([
    { id: 'aovivo', label: 'Ao vivo', icon: Activity },
    { id: 'historico', label: 'Histórico', icon: History },
  ]), []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (tab === 'historico') {
        params.set('tab', 'historico');
      } else {
        params.delete('tab');
      }
      return params;
    }, { replace: true });
  };

  // ========================================================================
  // HELPERS
  // ========================================================================

  const computed = statistics?.computed || {};
  const displayKpis = statistics?.display?.kpis || {};
  const displayValue = (key, fallbackValue = null, fallbackUnit = '') => ({
    value: displayKpis[key]?.value ?? fallbackValue,
    unit: displayKpis[key]?.unit ?? fallbackUnit,
  });
  const totalDistanceDisplay = displayValue('distance', computed.total_distance_km, 'km');
  const avgSpeedDisplay = displayValue('avg_speed', computed.avg_speed_kmh, 'km/h');
  const movingAvgSpeedDisplay = displayValue('moving_avg_speed', computed.moving_avg_speed_kmh, 'km/h');
  const totalFuelDisplay = displayValue('total_fuel', computed.total_fuel_liters, 'L');
  const operatingHoursDisplay = displayValue('operating_hours', computed.operating_hours, 'h');
  const movingTimeDisplay = displayValue('moving_time');
  const fuelTotalsByMotorDisplay = useMemo(() => {
    const displayFuelByMotor = statistics?.display?.fuel_by_motor || {};
    const valueFor = (...paths) => {
      for (const path of paths) {
        const display = displayFuelByMotor[path];
        if (display?.value !== null && display?.value !== undefined) {
          return display.value;
        }
      }
      return null;
    };

    return {
      port: valueFor('propulsion.port.fuel.rate', 'port'),
      starboard: valueFor('propulsion.starboard.fuel.rate', 'starboard'),
      engine1: valueFor('propulsion.engine1.fuel.rate', 'engine1'),
      total: totalFuelDisplay.value,
    };
  }, [statistics?.display?.fuel_by_motor, totalFuelDisplay.value]);

  // Interpreta a string do picker no `timezone` do operacional para que a
  // validação de range/limite use a mesma régua que vai pro backend.
  const customStartDate = parseDateTimeLocalValue(customStart, timezone);
  const customEndDate = parseDateTimeLocalValue(customEnd, timezone);
  const customRangeValid = customStartDate instanceof Date
    && customEndDate instanceof Date
    && Number.isFinite(customStartDate.getTime())
    && Number.isFinite(customEndDate.getTime())
    && customEndDate.getTime() > customStartDate.getTime()
    && (customEndDate.getTime() - customStartDate.getTime()) <= HISTORY_MAX_RANGE_MS;
  const customRangeTooLong = customStartDate instanceof Date
    && customEndDate instanceof Date
    && Number.isFinite(customStartDate.getTime())
    && Number.isFinite(customEndDate.getTime())
    && customEndDate.getTime() > customStartDate.getTime()
    && (customEndDate.getTime() - customStartDate.getTime()) > HISTORY_MAX_RANGE_MS;
  const customRangeDirty = customStart !== appliedCustomStart
    || customEnd !== appliedCustomEnd
    || appliedPeriod !== 'custom';

  const handlePeriodChange = (value) => {
    setPeriod(value);
    if (value !== 'custom') {
      setAppliedPeriod(value);
    }
  };

  const applyCustomRange = () => {
    if (!customRangeValid) return;
    setAppliedCustomStart(customStart);
    setAppliedCustomEnd(customEnd);
    setAppliedPeriod('custom');
  };
  
  // ========================================================================
  // RENDER - LOADING STATE
  // ========================================================================
  
  if (loadingEquipment && !equipment) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="w-full space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }
  
  // ========================================================================
  // RENDER - ERROR STATE
  // ========================================================================
  
  if (error && !equipment) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="w-full">
          <Card className="bg-destructive/10 border-destructive">
            <CardContent className="p-6 flex items-center gap-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <div>
                <h2 className="text-lg font-semibold">Erro</h2>
                <p className="text-muted-foreground">{error}</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/equipments')} className="ml-auto">
                Voltar
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // ========================================================================
  // RENDER - MAIN
  // ========================================================================
  
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b bg-card/50 backdrop-blur sticky top-0 z-10">
          <div className="w-full px-4 md:px-6 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Left: Back + Nome + Tag + Status */}
              <div className="flex items-center gap-4 min-w-0">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate('/equipments')}
                  data-testid="back-button"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold truncate">
                      {equipment?.name || 'Equipamento'}
                    </h1>
                    <Badge 
                      variant={equipment?.status === 'online' ? 'default' : 'secondary'}
                      className={equipment?.status === 'online' 
                        ? 'bg-green-500/10 text-green-500 border-green-500/30' 
                        : 'bg-gray-500/10 text-gray-400'
                      }
                      data-testid="equipment-status"
                    >
                      {equipment?.status === 'online' ? 'Online' : 'Offline'}
                    </Badge>
                    {/* v4.0: Agent Update Badge - click to open modal */}
                    <div 
                      className="cursor-pointer" 
                      onClick={() => setAgentModalOpen(true)}
                      data-testid="agent-update-badge-wrapper"
                    >
                      <AgentUpdateBadge equipmentId={equipmentId} />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {equipment?.tag || '—'}
                  </p>
                </div>
              </div>
              
              {/* Right: Heartbeat + Period selector + Refresh */}
              <div className="flex flex-wrap items-center justify-end gap-2">
                {/* D8 FIX: Heartbeat indicator */}
                <div 
                  className="flex items-center gap-1.5" 
                  title={`Última atualização: ${lastUpdate ? formatTime(lastUpdate) : 'N/A'}`}
                >
                  <span className={`h-2 w-2 rounded-full ${
                    lastUpdate && (Date.now() - lastUpdate.getTime()) < 60000
                      ? 'bg-green-500 animate-pulse'
                      : 'bg-yellow-500'
                  }`} />
                  <span className="text-xs text-muted-foreground hidden sm:inline">
                    {lastUpdate ? formatTime(lastUpdate) : '—'}
                  </span>
                </div>
                
                <Select value={period} onValueChange={handlePeriodChange}>
                  <SelectTrigger className="w-[150px]" data-testid="period-selector">
                    <Clock className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODS.map(p => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {period === 'custom' && (
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <span className="text-xs text-muted-foreground">De</span>
                      <DateTimePicker24h
                        value={customStart}
                        onChange={setCustomStart}
                        ariaLabel="Início do intervalo histórico"
                      />
                      <span className="text-xs text-muted-foreground">Até</span>
                      <DateTimePicker24h
                        value={customEnd}
                        onChange={setCustomEnd}
                        ariaLabel="Fim do intervalo histórico"
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="h-9"
                        onClick={applyCustomRange}
                        disabled={!customRangeValid || !customRangeDirty || loadingStats}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Confirmar
                      </Button>
                    </div>
                    {customRangeTooLong && (
                      <span className="text-xs text-destructive">Máximo de 90 dias</span>
                    )}
                  </div>
                )}
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPrintDialogOpen(true)}
                  disabled={loadingSources || loadingStats || loadingEquipment}
                  title="Exportar relatório em PDF"
                  data-testid="print-button"
                >
                  <Printer className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    fetchLiveView();
                    fetchStatistics();
                    setRefreshNonce(n => n + 1);
                  }}
                  disabled={loadingSources || loadingStats}
                  data-testid="refresh-button"
                >
                  <RefreshCw className={`h-4 w-4 ${(loadingSources || loadingStats) ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              {activeTab === 'historico' && (showStatsLoadingHint || statsError) && (
                <div className="mt-2 flex items-center justify-end gap-2 text-xs text-muted-foreground">
                  {showStatsLoadingHint && !statsError ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Carregando histórico do período selecionado...</span>
                    </>
                  ) : (
                    <span className="text-destructive">{statsError}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="w-full px-4 md:px-6 py-6 space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpis-section">
            <KPICard 
              icon={MapPin}
              label="Distância"
              value={totalDistanceDisplay.value}
              unit={totalDistanceDisplay.unit}
              loading={loadingStats}
            />
            <KPICard 
              icon={Gauge}
              label="Vel. Média"
              value={avgSpeedDisplay.value}
              unit={avgSpeedDisplay.unit}
              secondaryItems={[
                {
                  label: 'Média em movimento',
                  value: movingAvgSpeedDisplay.value,
                  unit: movingAvgSpeedDisplay.unit,
                },
              ]}
              loading={loadingStats}
            />
            <KPICard 
              icon={Fuel}
              label="Consumo"
              value={totalFuelDisplay.value}
              unit={totalFuelDisplay.unit}
              loading={loadingStats}
            />
            <KPICard 
              icon={Clock}
              label="Operação"
              value={operatingHoursDisplay.value}
              unit={operatingHoursDisplay.unit}
              secondaryItems={[
                {
                  label: 'Tempo em movimento',
                  value: movingTimeDisplay.value,
                  unit: movingTimeDisplay.unit,
                },
              ]}
              loading={loadingStats}
            />
          </div>
          
          {/* Tabs fixas: Ao vivo (default) + Histórico */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="w-full justify-start overflow-x-auto" data-testid="tabs-list">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="flex items-center gap-2"
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {/* Tab Ao vivo — seções montadas pelo cadastro (live-view) */}
            <TabsContent value="aovivo" className="mt-6">
              {loadingSources && !liveView ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Skeleton className="h-64" />
                  <Skeleton className="h-64" />
                </div>
              ) : (
                <LiveTab
                  equipmentId={equipmentId}
                  equipment={equipment}
                  liveView={liveView}
                  loadingLive={loadingSources}
                  geofences={geofences}
                  refreshNonce={refreshNonce}
                />
              )}
            </TabsContent>

            {/* Tab Histórico */}
            <TabsContent value="historico" className="mt-6">
              <TelemetryCharts
                equipmentId={equipmentId}
                period={appliedPeriod}
                timeRange={timeRange}
                fuelTotalsByMotor={fuelTotalsByMotorDisplay}
                totalFuelValue={totalFuelDisplay.value}
                totalFuelUnit={totalFuelDisplay.unit}
                refreshNonce={refreshNonce}
              />
            </TabsContent>
          </Tabs>
          
          {/* Last update indicator */}
          {lastUpdate && (
            <p className="text-xs text-muted-foreground text-center">
              Última atualização: {formatTime(lastUpdate)}
            </p>
          )}
        </div>
      </div>
      
      {/* v4.0: Agent Update Modal */}
      <AgentUpdateModal
        equipmentId={equipmentId}
        equipmentTag={equipment?.tag}
        isOpen={agentModalOpen}
        onClose={() => setAgentModalOpen(false)}
      />

      {/* Exportação em PDF */}
      <PrintExportDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        availableCharts={PRINT_CHART_OPTIONS.map(c => ({ id: c.id, label: c.label }))}
        defaultSelectedCharts={PRINT_DEFAULT_SELECTED}
        defaultTheme="light"
        defaultColumns={2}
        onConfirm={({ theme, columns, charts }) => {
          setPrintDialogOpen(false);
          // Renderiza o relatório como overlay invisível na própria aba:
          // o navegador imprime só esse overlay e o estado do monitor é
          // preservado. Evita o problema de sessão de nova aba (o token
          // vive em sessionStorage, que não cruza abas).
          setPrintConfig({
            period: appliedPeriod,
            start: timeRange?.start,
            stop: timeRange?.stop,
            theme,
            columns,
            charts,
          });
        }}
      />

      {/* Overlay de impressão (invisível na tela; renderiza só no @media print). */}
      {printConfig && (
        <EquipmentMonitorPrint
          embeddedEquipmentId={equipmentId}
          embeddedConfig={printConfig}
          onDone={() => setPrintConfig(null)}
        />
      )}
    </ErrorBoundary>
  );
};

export default EquipmentMonitorPage;
